/**
 * Video Section Lazy Loader
 * Coordinates lazy loading of video sections for optimal performance
 * - Only initializes video sections when they come into view
 * - Prevents duplicate initializations  
 * - Handles Shopify theme editor events
 */
import VideoSection from './VideoSection.js';

(function() {
  'use strict';
  
  // Prevent multiple initializations
  if (window.VideoSectionLazyLoader) {
    return;
  }
  
  // Make VideoSection available globally for compatibility
  window.VideoSection = VideoSection;
  
  // Initialize video sections with lazy loading
  function setupLazyVideoInit() {
    console.log('🎬 [VideoSection] Setting up lazy video init...');
    
    // Support both video-section and video-with-content sections
    const videoSections = document.querySelectorAll('[data-video-lazy-init="true"]:not([data-video-observer-attached]):not([data-video-section-initialized]), [data-section-type="video"]:not([data-video-observer-attached]):not([data-video-section-initialized]), [data-section-type="video-with-content"]:not([data-video-observer-attached]):not([data-video-section-initialized])');
    
    console.log('🎬 [VideoSection] Found video sections:', videoSections.length);
    
    if (videoSections.length === 0) {
      console.log('🎬 [VideoSection] No video sections found to initialize');
      return;
    }
    
    // Use Intersection Observer to detect when video sections become visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        console.log('🎬 [VideoSection] Intersection observer triggered:', {
          isIntersecting: entry.isIntersecting,
          sectionId: entry.target.dataset.sectionId,
          sectionType: entry.target.dataset.sectionType
        });
        
        if (entry.isIntersecting) {
          console.log('🎬 [VideoSection] Initializing VideoSection for:', entry.target.dataset.sectionId);
          // Check if already initialized to prevent duplicates
          if (!entry.target.hasAttribute('data-video-section-initialized')) {
            new VideoSection(entry.target);
            entry.target.setAttribute('data-video-section-initialized', 'true');
          }
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '200px 0px' // Start loading 50px before the section becomes visible
    });
    
    videoSections.forEach(section => {
      section.setAttribute('data-video-observer-attached', 'true');
      observer.observe(section);
      
      // Also initialize on any user interaction with the section
      section.addEventListener('click', () => {
        // Check if already initialized to prevent duplicates
        if (!section.hasAttribute('data-video-section-initialized')) {
          new VideoSection(section);
          section.setAttribute('data-video-section-initialized', 'true');
        }
        observer.unobserve(section);
      }, { once: true });
    });
  }
  
  // Public API
  window.VideoSectionLazyLoader = {
    init: setupLazyVideoInit
  };
  
  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.VideoSectionLazyLoader.init();
    });
  } else {
    window.VideoSectionLazyLoader.init();
  }
  
  // Handle Shopify theme editor section reloads
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId) {
      const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
      if (section && (section.hasAttribute('data-video-lazy-init') || 
                      section.dataset.sectionType === 'video' || 
                      section.dataset.sectionType === 'video-with-content')) {
        window.VideoSectionLazyLoader.init();
      }
    }
  });
  
})();

console.log('🎬 [VideoSection] Lazy loader initialized'); 